import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight, Receipt, UserCheck, TrendingDown, Truck } from "lucide-react";

/**
 * MultiSitesBlock — dedicated reassurance for the high-value B2B segment
 * (franchises, multi-sites, grands comptes). Currently invisible on the
 * site: only Sophie M.'s testimonial mentions "12 sites".
 *
 * Position on the Home: between KPIsCounters and TestimonialsCarouselLogos.
 * The 4 cards answer the four objections a procurement lead raises before
 * a callback: facturation, interlocuteur, prix, logistique.
 */
const CARDS = [
  {
    icon: Receipt,
    title: "Compte centralisé",
    text: "Facturation unique, paiement consolidé, reporting volume mensuel multi-sites. Une seule comptabilité pour tous vos restaurants.",
  },
  {
    icon: UserCheck,
    title: "Account manager dédié",
    text: "Un interlocuteur unique pour vos 3 à 50 points de vente, joignable directement par téléphone et email.",
  },
  {
    icon: TrendingDown,
    title: "Tarif réseau",
    text: "Grille dégressive activable à partir de 3 sites ou d'un engagement annuel. Conditions négociées à l'ouverture du compte cadre.",
  },
  {
    icon: Truck,
    title: "Livraison synchronisée",
    text: "Tournées coordonnées sur l'ensemble de vos sites quand la géographie le permet. Même créneau, même camion, moins d'opérationnel pour vos équipes.",
  },
];

export const MultiSitesBlock = () => (
  <section
    data-testid="multi-sites-block"
    className="relative bg-fd-bg border-t border-fd-border py-24 lg:py-32 px-6 md:px-10 lg:px-14 overflow-hidden"
  >
    <div className="absolute -top-32 -right-32 w-[500px] h-[500px] bg-fd-gold opacity-[0.04] blur-3xl rounded-full pointer-events-none"></div>

    <div className="relative max-w-[1400px] mx-auto">
      <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-start">
        <div className="lg:col-span-5 lg:sticky lg:top-32">
          <div className="mono-label-gold mb-5 flex items-center gap-3">
            <span className="w-8 h-px bg-fd-gold"></span>
            <span>Multi-sites · Franchises · Grands comptes</span>
          </div>
          <h2 className="font-display display-xl text-4xl md:text-5xl lg:text-6xl uppercase tracking-tighter leading-[0.95]">
            Une seule facture.{" "}
            <span className="block text-gradient-gold">Tous vos points de vente.</span>
          </h2>
          <p className="mt-6 font-body text-zinc-300 text-base md:text-lg leading-relaxed max-w-xl">
            Pour les enseignes qui ouvrent un 3<sup>e</sup> restaurant, les franchisés référencés et les acheteurs de réseaux, Frites Dorées propose un compte cadre dédié — un interlocuteur, un tarif négocié, une logistique alignée sur vos rythmes.
          </p>
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="mt-8"
          >
            <Link
              to="/contact?interest=multi-sites"
              data-cursor-hover
              data-testid="multi-sites-cta"
              className="btn-pill btn-primary-v2"
            >
              Demander une proposition multi-sites <ArrowUpRight size={16} />
            </Link>
          </motion.div>
        </div>

        <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-6">
          {CARDS.map(({ icon: Icon, title, text }, i) => (
            <motion.div
              key={title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.6, delay: 0.1 + i * 0.08 }}
              className="border border-white/10 p-5 md:p-6 bg-fd-elevated hover:border-fd-gold/40 transition-colors"
            >
              <div className="w-10 h-10 mb-4 border border-white/15 flex items-center justify-center">
                <Icon size={16} className="text-fd-gold" />
              </div>
              <p className="font-display text-base md:text-lg uppercase tracking-tight mb-2">{title}</p>
              <p className="font-body text-sm text-zinc-400 leading-relaxed">{text}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  </section>
);
